import { type ApolloError } from '@apollo/client/errors'
import { useGetProfilePhoneNumberLazyQuery } from '@kijiji/generated/graphql-types'
import { useReCaptcha } from 'next-recaptcha-v3'
import { useState } from 'react'

import { type ProfilePhoneNumberProps } from '@/features/profile/components/phone-number/ProfilePhoneNumber'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'

const CONTACT_SELLER_RECAPTCHA_EVENT = 'CONTACT_SELLER'

type UseProfilePhoneNumberResult = {
  /**
   * The phone number of the listing. If the phone number has not been revealed,
   * this will be `null`.
   */
  revealedPhoneNumber: string | null

  /**
   * The current loading state of revealing the phone number.
   */
  isRevealPhoneNumberLoading: boolean

  /**
   * The current error state of revealing the phone number.
   */
  revealPhoneNumberError: ApolloError | undefined

  /**
   * Function to reveal the phone number.
   */
  triggerPhoneNumberReveal: () => Promise<void>

  /**
   * Function to trigger the call tracking event.
   *
   * This should be called when the user clicks the link to call the seller
   * after the phone number has been revealed.
   */
  triggerCallTracking: () => void
}

/**
 * Custom hook that fetches the phone number of a seller. Handles Google
 * reCAPTCHA, tracking, and loading states.
 *
 * @returns See return type for details of what's exposed.
 */
export function useProfilePhoneNumber({
  profileId,
  listingId,
  trackingLabel,
}: ProfilePhoneNumberProps): UseProfilePhoneNumberResult {
  const { executeRecaptcha } = useReCaptcha()
  const [
    revealPhoneNumber,
    { loading: isRevealPhoneNumberLoading, error: revealPhoneNumberError },
  ] = useGetProfilePhoneNumberLazyQuery()
  const [revealedPhoneNumber, setRevealedPhoneNumber] = useState<string | null>(null)

  /**
   * This is returned by the hook and is used to reveal the phone number.
   */
  const triggerPhoneNumberReveal = async () => {
    if (revealedPhoneNumber) return

    const recaptchaToken = await executeRecaptcha(CONTACT_SELLER_RECAPTCHA_EVENT)
    const { data } = await revealPhoneNumber({
      variables: { profileId, listingId, recaptchaToken },
    })

    if (data?.profile.phoneNumber) {
      setRevealedPhoneNumber(data.profile.phoneNumber)

      trackEvent({
        action: GA_EVENT.R2SPhoneReveal,
        label: trackingLabel,
      })
    }
  }

  /**
   * This is returned by the hook and is used to trigger the call tracking event.
   */
  const triggerCallTracking = () => {
    trackEvent({
      action: GA_EVENT.R2SPhoneBegin,
      label: trackingLabel,
    })
  }

  return {
    revealedPhoneNumber,
    isRevealPhoneNumberLoading,
    revealPhoneNumberError,
    triggerPhoneNumberReveal,
    triggerCallTracking,
  }
}
